import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { validate as uuidValidate, version as uuidVersion } from "uuid";

const useSurveyValidation = ({
  endpoint,
  payload,
  componentName,
  interactionIdKey,
  initialStatus = "loading",
}) => {
  const [validationStatus, setValidationStatus] = useState(initialStatus);

  const isValidUuid =
    uuidValidate(interactionIdKey) && uuidVersion(interactionIdKey) === 4;

  const defaultPayload = useMemo(
    () => ({ session_identifier: interactionIdKey }),
    [interactionIdKey],
  );

  const resolvedPayload = payload ?? defaultPayload;

  useEffect(() => {
    if (!isValidUuid) {
      setValidationStatus("sessionInvalid");
      return;
    }

    if (!endpoint && import.meta.env.DEV) {
      setValidationStatus("valid");
      return;
    }

    const validate = async () => {
      try {
        await axios.post(endpoint, resolvedPayload, { timeout: 30000 });
        setValidationStatus("valid");
      } catch (error) {
        const statusCode = error.response?.status;

        if (statusCode === 400) {
          setValidationStatus("sessionInvalid");
          return;
        }

        if (statusCode === 409) {
          setValidationStatus("alreadySubmitted");
          return;
        }

        setValidationStatus("internalServerError");

        Sentry.captureException(error, {
          tags: { component: componentName, action: "validate_form" },
          extra: {
            interactionIdKey,
            statusCode,
            responseData: error.response?.data,
          },
          level: "error",
        });
      }
    };

    validate();
    // resolvedPayload and componentName are intentionally omitted: resolvedPayload
    // is stable (memoized internally or passed from caller), and componentName is
    // always a string constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, interactionIdKey, isValidUuid]);

  return validationStatus;
};

export default useSurveyValidation;
